import React, { useState } from "react";
import { Sparkles, Check, Send, X, Loader2, MessageSquareReply, UserPlus, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError, arr } from "@/lib/api";
import Stars from "@/components/common/Stars";
import StatusBadge from "@/components/common/StatusBadge";
import { useAuth } from "@/context/AuthContext";

export default function ReviewItem({ review, onChange }) {
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";
  const [reply, setReply] = useState(review.finalReply || review.aiReply || "");
  const [options, setOptions] = useState(review.aiReplyOptions || []);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(review.replyStatus === "pending_approval");

  const call = async (fn, key) => {
    setBusy(key);
    try { await fn(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const generate = () => call(async () => {
    const { data } = await api.post(`/reviews/${review.id}/generate-reply`);
    setOptions(arr(data.options));
    setReply(data.review.aiReply || "");
    setOpen(true);
    toast.success(`AI reply generated (${data.source})`);
    onChange?.();
  }, "gen");

  const saveDraft = () => call(async () => {
    await api.put(`/reviews/${review.id}/reply`, { reply, status: "draft" });
    toast.success("Saved as draft");
    onChange?.();
  }, "draft");

  const approve = () => call(async () => {
    await api.post(`/reviews/${review.id}/approve-reply`, { reply });
    toast.success("Reply approved");
    onChange?.();
  }, "approve");

  const publish = () => call(async () => {
    await api.post(`/reviews/${review.id}/publish-reply`, { reply });
    toast.success("Reply published to Google");
    onChange?.();
  }, "publish");

  const reject = () => call(async () => {
    await api.post(`/reviews/${review.id}/reject-reply`);
    toast.success("Reply rejected");
    onChange?.();
  }, "reject");

  const markPriority = () => call(async () => {
    await api.post(`/reviews/${review.id}/priority`, { priority: "urgent" });
    toast.success("Marked urgent");
    onChange?.();
  }, "prio");

  return (
    <div className="card-surface p-5" data-testid={`review-${review.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 font-semibold text-brand-700">
            {review.reviewerName?.[0]?.toUpperCase() || "A"}
          </div>
          <div>
            <p className="font-semibold text-slate-800">{review.reviewerName}</p>
            <div className="mt-0.5 flex items-center gap-2">
              <Stars rating={review.rating} />
              <span className="text-xs text-slate-400">{new Date(review.reviewDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={review.sentiment} />
          {review.priority !== "normal" && <StatusBadge status={review.priority} />}
          <StatusBadge status={review.replyStatus} />
        </div>
      </div>

      <p className="mt-3 text-sm text-slate-600">{review.comment || <span className="italic text-slate-400">No comment</span>}</p>

      {review.existingReply && (
        <div className="mt-3 rounded-xl border border-green-100 bg-green-50/60 p-3">
          <p className="text-xs font-semibold text-green-700">Published reply</p>
          <p className="mt-1 text-sm text-slate-600">{review.existingReply}</p>
        </div>
      )}

      {!readOnly && review.replyStatus !== "published" && (
        <div className="mt-4">
          {!open ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={generate} disabled={busy === "gen"} className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" data-testid={`generate-reply-${review.id}`}>
                {busy === "gen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1 h-4 w-4" /> Generate AI Reply</>}
              </Button>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setOpen(true)}><MessageSquareReply className="mr-1 h-4 w-4" /> Write Reply</Button>
              <Button size="sm" variant="outline" className="rounded-xl" onClick={markPriority}><Flag className="mr-1 h-4 w-4" /> Mark Urgent</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {options.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {options.map((o, i) => (
                    <button key={i} onClick={() => setReply(o)} className="max-w-xs truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 hover:border-brand-400 hover:text-brand-700" title={o}>
                      Option {i + 1}
                    </button>
                  ))}
                  <Button size="sm" variant="ghost" onClick={generate} disabled={busy === "gen"} className="h-7 text-xs text-brand-600">
                    {busy === "gen" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Regenerate"}
                  </Button>
                </div>
              )}
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Write your reply..." className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200" data-testid={`reply-textarea-${review.id}`} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-xl" onClick={saveDraft} disabled={busy === "draft"}>Save Draft</Button>
                <Button size="sm" variant="outline" className="rounded-xl" onClick={approve} disabled={busy === "approve"}><Check className="mr-1 h-4 w-4" /> Approve</Button>
                <Button size="sm" onClick={publish} disabled={busy === "publish" || !reply} className="rounded-xl bg-green-600 text-white hover:bg-green-700" data-testid={`publish-reply-${review.id}`}>
                  {busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1 h-4 w-4" /> Publish</>}
                </Button>
                <Button size="sm" variant="ghost" className="rounded-xl text-red-600 hover:bg-red-50" onClick={reject}><X className="mr-1 h-4 w-4" /> Reject</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
